var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { EventEmitter } from './core/EventEmitter';
import { Matrix2D, degToRad, uid, merge } from './utils';
import { AnimationEngine } from './animation/Animation';
var Node = /** @class */ (function (_super) {
    __extends(Node, _super);
    function Node(type, options) {
        if (options === void 0) { options = {}; }
        var _a;
        var _this = _super.call(this) || this;
        _this.name = '';
        _this.x = 0;
        _this.y = 0;
        _this.rotation = 0;
        _this.scaleX = 1;
        _this.scaleY = 1;
        _this.skewX = 0;
        _this.skewY = 0;
        _this.opacity = 1;
        _this.visible = true;
        _this.zIndex = 0;
        _this.listening = true;
        _this.draggable = false;
        _this.dropTarget = false;
        _this.dragPayload = undefined;
        _this.focusable = false;
        _this.tabIndex = 0;
        _this.fill = '#000000';
        _this.stroke = null;
        _this.strokeWidth = 1;
        _this.lineCap = 'butt';
        _this.lineJoin = 'miter';
        _this.dash = [];
        _this.dashOffset = 0;
        _this.shadow = null;
        _this.clip = false;
        _this.mask = null;
        _this.metadata = {};
        _this.parent = null;
        _this._dirty = true;
        _this._worldMatrix = new Matrix2D();
        _this._localMatrix = new Matrix2D();
        _this.type = type;
        _this.id = (_a = options.id) !== null && _a !== void 0 ? _a : uid(type);
        _this.applyOptions(options);
        AnimationEngine.onFrame = function () { var _a; return (_a = _this.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender(); };
        return _this;
    }
    Node.prototype.applyOptions = function (options) {
        if (options.name)
            this.name = options.name;
        if (options.x !== undefined)
            this.x = options.x;
        if (options.y !== undefined)
            this.y = options.y;
        if (options.rotation !== undefined)
            this.rotation = options.rotation;
        if (options.scaleX !== undefined)
            this.scaleX = options.scaleX;
        if (options.scaleY !== undefined)
            this.scaleY = options.scaleY;
        if (options.skewX !== undefined)
            this.skewX = options.skewX;
        if (options.skewY !== undefined)
            this.skewY = options.skewY;
        if (options.opacity !== undefined)
            this.opacity = options.opacity;
        if (options.visible !== undefined)
            this.visible = options.visible;
        if (options.zIndex !== undefined)
            this.zIndex = options.zIndex;
        if (options.fill !== undefined)
            this.fill = options.fill;
        if (options.stroke !== undefined)
            this.stroke = options.stroke;
        if (options.strokeWidth !== undefined)
            this.strokeWidth = options.strokeWidth;
        if (options.lineCap)
            this.lineCap = options.lineCap;
        if (options.lineJoin)
            this.lineJoin = options.lineJoin;
        if (options.dash)
            this.dash = options.dash;
        if (options.dashOffset !== undefined)
            this.dashOffset = options.dashOffset;
        if (options.shadow !== undefined)
            this.shadow = options.shadow;
        if (options.clip !== undefined)
            this.clip = options.clip;
        if (options.mask !== undefined)
            this.mask = options.mask;
        if (options.listening !== undefined)
            this.listening = options.listening;
        if (options.draggable !== undefined)
            this.draggable = options.draggable;
        if (options.dropTarget !== undefined)
            this.dropTarget = options.dropTarget;
        if (options.dragPayload !== undefined)
            this.dragPayload = options.dragPayload;
        if (options.focusable !== undefined)
            this.focusable = options.focusable;
        if (options.tabIndex !== undefined)
            this.tabIndex = options.tabIndex;
        if (options.role !== undefined)
            this.role = options.role;
        if (options.ariaChecked !== undefined)
            this.ariaChecked = options.ariaChecked;
        if (options.ariaValueNow !== undefined)
            this.ariaValueNow = options.ariaValueNow;
        if (options.ariaValueMin !== undefined)
            this.ariaValueMin = options.ariaValueMin;
        if (options.ariaValueMax !== undefined)
            this.ariaValueMax = options.ariaValueMax;
        if (options.ariaLive !== undefined)
            this.ariaLive = options.ariaLive;
        if (options.metadata)
            this.metadata = __assign({}, options.metadata);
        merge(this, options);
        this.markDirty();
    };
    Object.defineProperty(Node.prototype, "scale", {
        get: function () {
            return this.scaleX;
        },
        set: function (value) {
            this.scaleX = value;
            this.scaleY = value;
            this.markDirty();
        },
        enumerable: false,
        configurable: true
    });
    /** Position helpers */
    Node.prototype.position = function (x, y) {
        this.x = x;
        this.y = y;
        this.markDirty();
        return this;
    };
    Node.prototype.move = function (x, y) {
        return this.position(x, y);
    };
    Node.prototype.translate = function (dx, dy) {
        this.x += dx;
        this.y += dy;
        this.markDirty();
        return this;
    };
    Node.prototype.rotate = function (degrees) {
        this.rotation = degrees;
        this.markDirty();
        return this;
    };
    Node.prototype.setOpacity = function (value) {
        this.opacity = value;
        this.markDirty();
        return this;
    };
    Node.prototype.hide = function () {
        this.visible = false;
        this.markDirty();
        return this;
    };
    Node.prototype.show = function () {
        this.visible = true;
        this.markDirty();
        return this;
    };
    Node.prototype.attr = function (key, value) {
        if (value === undefined) {
            return this[key];
        }
        this[key] = value;
        this.markDirty();
        return this;
    };
    Node.prototype.animate = function (options) {
        return AnimationEngine.animate(this, options);
    };
    Node.prototype.markDirty = function () {
        var _a, _b, _c;
        this._dirty = true;
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.markDirty();
        (_b = this.getApp()) === null || _b === void 0 ? void 0 : _b.markNodeDirty(this);
        (_c = this.getApp()) === null || _c === void 0 ? void 0 : _c.requestRender();
    };
    Node.prototype.isDirty = function () {
        return this._dirty;
    };
    Node.prototype.clearDirty = function () {
        this._dirty = false;
    };
    Node.prototype.getLocalMatrix = function () {
        if (this._dirty) {
            this._localMatrix.identity();
            this._localMatrix.translate(this.x, this.y);
            this._localMatrix.rotate(degToRad(this.rotation));
            this._localMatrix.scale(this.scaleX, this.scaleY);
            if (this.skewX || this.skewY) {
                this._localMatrix.skew(degToRad(this.skewX), degToRad(this.skewY));
            }
        }
        return this._localMatrix;
    };
    Node.prototype.getWorldMatrix = function () {
        var local = this.getLocalMatrix();
        if (this.parent) {
            var parentWorld = this.parent.getWorldMatrix();
            this._worldMatrix.a = parentWorld.a;
            this._worldMatrix.b = parentWorld.b;
            this._worldMatrix.c = parentWorld.c;
            this._worldMatrix.d = parentWorld.d;
            this._worldMatrix.e = parentWorld.e;
            this._worldMatrix.f = parentWorld.f;
            this._worldMatrix.multiply(local);
        }
        else {
            this._worldMatrix.a = local.a;
            this._worldMatrix.b = local.b;
            this._worldMatrix.c = local.c;
            this._worldMatrix.d = local.d;
            this._worldMatrix.e = local.e;
            this._worldMatrix.f = local.f;
        }
        return this._worldMatrix;
    };
    Node.prototype.getTransformState = function () {
        return {
            x: this.x,
            y: this.y,
            rotation: this.rotation,
            scaleX: this.scaleX,
            scaleY: this.scaleY,
            skewX: this.skewX,
            skewY: this.skewY,
            opacity: this.opacity,
        };
    };
    Node.prototype.getApp = function () {
        var direct = this._app;
        if (direct)
            return direct;
        var node = this.parent;
        while (node) {
            var app = node._app;
            if (app)
                return app;
            node = node.parent;
        }
        return null;
    };
    Node.prototype.toJSON = function () {
        return {
            type: this.type,
            id: this.id,
            props: __assign({ x: this.x, y: this.y, rotation: this.rotation, scaleX: this.scaleX, scaleY: this.scaleY, opacity: this.opacity, visible: this.visible, fill: this.fill, stroke: this.stroke, strokeWidth: this.strokeWidth, metadata: this.metadata }, this.getShapeProps()),
        };
    };
    Node.prototype.getShapeProps = function () {
        return {};
    };
    Node.prototype.destroy = function () {
        var _a;
        (_a = this.getApp()) === null || _a === void 0 ? void 0 : _a.onNodeDestroyed(this);
        this.removeAllListeners();
        this.parent = null;
    };
    return Node;
}(EventEmitter));
export { Node };
